import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Facebook, Instagram } from "lucide-react";

const XIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const socials = [
  {
    label: "X",
    href: "https://x.com/TheOfficialKPL",
    render: (size) => <XIcon size={size} />,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/kenyanpremierleague",
    render: (size) => <Instagram size={size} strokeWidth={1.8} />,
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/TheOfficialKPL",
    render: (size) => <Facebook size={size} strokeWidth={1.8} />,
  },
];

export const SocialSidebar = () => {
  return (
    <motion.aside
      data-testid="social-sidebar"
      initial={{ opacity: 0, x: -12, y: "-50%" }}
      animate={{ opacity: 1, x: 0, y: "-50%" }}
      transition={{ delay: 0.6, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="fixed left-0 top-1/2 z-40 hidden sm:flex flex-col items-center"
      aria-label="Follow Kenya Premier League on social media"
    >
      {/* Vertical FOLLOW label */}
      <span
        className="text-[9px] font-bold uppercase tracking-[0.4em] text-[#0A0A0A]/55 mb-4 [writing-mode:vertical-rl] rotate-180"
        data-testid="social-sidebar-label"
      >
        Follow
      </span>

      {/* Subtle top rule */}
      <span className="block w-px h-8 bg-[#0A0A0A]/15" aria-hidden="true" />

      <div className="flex flex-col bg-white border border-[#E4E4E7] shadow-sm">
        {socials.map((s) => (
          <a
            key={s.label}
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={s.label}
            data-testid={`social-sidebar-${s.label.toLowerCase()}`}
            className="group inline-flex items-center justify-center w-11 h-11 border-b last:border-b-0 border-[#E4E4E7] text-[#0A0A0A]/70 hover:text-white hover:bg-[#D1111A] transition-colors"
          >
            {s.render(16)}
          </a>
        ))}
      </div>

      <span className="block w-px h-8 bg-[#0A0A0A]/15" aria-hidden="true" />
    </motion.aside>
  );
};

/**
 * Compact bottom-right pill for phones (< sm breakpoint). Keeps the same
 * three icons within thumb reach without taking any horizontal space.
 * Hides itself while the mobile hamburger drawer is open (which locks
 * body scroll — we watch document.body.style.overflow as the signal).
 */
export const SocialFloatMobile = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const check = () => setDrawerOpen(document.body.style.overflow === "hidden");
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { attributes: true, attributeFilter: ["style"] });
    return () => observer.disconnect();
  }, []);

  if (drawerOpen) return null;

  return (
    <motion.div
      data-testid="social-float-mobile"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7, duration: 0.4 }}
      className="fixed bottom-4 right-4 z-40 sm:hidden flex flex-col bg-white border border-[#E4E4E7] shadow-lg"
    >
      {socials.map((s) => (
        <a
          key={s.label}
          href={s.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={s.label}
          data-testid={`social-float-${s.label.toLowerCase()}`}
          className="inline-flex items-center justify-center w-10 h-10 border-b last:border-b-0 border-[#E4E4E7] text-[#0A0A0A]/70 active:text-white active:bg-[#D1111A] transition-colors"
        >
          {s.render(16)}
        </a>
      ))}
    </motion.div>
  );
};
